import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FieldArray, FormikErrors, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import Switch from '@mui/material/Switch';
import DialogActions from '@mui/material/DialogActions';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import FormHelperText from '@mui/material/FormHelperText';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { UploadFileButton } from '../../components/UploadFileButton';
import { ProductTypeIndexFundsAutocomplete } from '../../components/ProductTypeIndexFundsAutocomplete/ProductTypeIndexFundsAutocomplete';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { productTypeFormFields } from '../../forms/fields/formFields';
import { productTypeFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import {
  createProductType,
  fetchProductTypeFileTypeOptions,
  updateProductType,
} from '../../redux/modules/productType/product-type.actions';
import { productTypeSelector } from '../../redux/modules/productType/product-type.selectors';
import {
  CreateProductTypeRequest,
  ProductStatus,
  ProductType,
  ProductTypeFile,
  UpdateProductTypeFile,
  UpdateProductTypeRequest,
} from '../../types/productType';
import { UploadEntity } from '../../types/media';

interface ProductTypeDialogProps {
  open: boolean;
  initialValue: ProductType | null;
  onClose: (isReload: boolean) => void;
}

export function ProductTypeDialog({ open, initialValue, onClose }: ProductTypeDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      [productTypeFormFields.nameEn]: '',
      [productTypeFormFields.nameAr]: '',
      [productTypeFormFields.descriptionEn]: '',
      [productTypeFormFields.descriptionAr]: '',
      [productTypeFormFields.disabled]: false,
      [productTypeFormFields.scoreDependent]: false,
      [productTypeFormFields.isCustom]: false,
      [productTypeFormFields.fee]: 0,
      [productTypeFormFields.files]: [] as ProductTypeFile[],
      [productTypeFormFields.indexFunds]: [] as number[],
      [productTypeFormFields.productStatus]: '',
    },
    validationSchema: productTypeFormSchema,
    onSubmit: (formValues) => {
      const indexFunds = formValues.indexFunds ?? [];
      const productStatus = formValues.productStatus ? (formValues.productStatus as ProductStatus) : undefined;

      if (initialValue?.id) {
        const updatePayload: UpdateProductTypeRequest = {
          id: initialValue.id,
          nameEn: formValues.nameEn,
          nameAr: formValues.nameAr,
          descriptionEn: formValues.descriptionEn,
          descriptionAr: formValues.descriptionAr,
          disabled: formValues.disabled,
          scoreDependent: formValues.scoreDependent,
          isCustom: formValues.isCustom,
          fee: formValues.fee,
          files: formValues.files,
          indexFunds,
          productStatus,
        };
        dispatch(updateProductType(updatePayload));
        return;
      }

      const payload: CreateProductTypeRequest = {
        id: undefined,
        nameEn: formValues.nameEn,
        nameAr: formValues.nameAr,
        descriptionEn: formValues.descriptionEn,
        descriptionAr: formValues.descriptionAr,
        disabled: formValues.disabled,
        scoreDependent: formValues.scoreDependent,
        isCustom: formValues.isCustom,
        fee: formValues.fee,
        files: formValues.files,
        indexFunds,
        productStatus,
      };
      dispatch(createProductType(payload));
    },
  });

  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, resetForm, setFieldValue } =
    formik;
  const { createStatus, updateStatus, error, productTypeFileTypeOptions } = useSelector(productTypeSelector);

  useEffect(() => {
    if (!open) {
      resetForm();
    }

    dispatch(fetchProductTypeFileTypeOptions());
  }, [open]);

  const getDefaultFiles = () =>
    productTypeFileTypeOptions.map((ft) => ({
      fileType: ft.id,
      documentLink: '',
      isPublic: true,
    }));

  // Depend on the file type ids rather than the array itself: the options are refetched on every
  // open, so the array reference changes even when the available file types are identical.
  const fileTypeIdsKey = productTypeFileTypeOptions.map((ft) => ft.id).join(',');

  useEffect(() => {
    if (!initialValue) return;

    const existingFiles =
      initialValue.files?.map((f) => ({
        id: f?.id,
        documentLink: f.documentLink,
        fileType: (f.fileType as any)?.id,
      })) || [];

    getDefaultFiles().forEach((df) => {
      if (!existingFiles.find((f) => f.fileType === df.fileType)) {
        existingFiles.unshift({ ...df, id: 0 });
      }
    });

    existingFiles.sort((a, b) => a.fileType - b.fileType);

    setValues({
      nameEn: initialValue?.nameEn || '',
      nameAr: initialValue?.nameAr || '',
      descriptionEn: initialValue?.descriptionEn || '',
      descriptionAr: initialValue?.descriptionAr || '',
      disabled: initialValue?.disabled || false,
      scoreDependent: initialValue?.scoreDependent || false,
      isCustom: initialValue?.isCustom || false,
      fee: initialValue?.fee || 0,
      files: existingFiles,
      indexFunds: (() => {
        if (!initialValue?.indexFunds) return [];
        return initialValue.indexFunds.map((indexFund: number | { id: number }) => {
          if (typeof indexFund === 'number') return indexFund;
          return indexFund.id;
        });
      })(),
      productStatus:
        initialValue?.productStatus &&
        Object.values(ProductStatus).includes(initialValue.productStatus as ProductStatus)
          ? (initialValue.productStatus as ProductStatus)
          : '',
    });
  }, [initialValue, fileTypeIdsKey]);

  const getFileTypeName = (id: number) => productTypeFileTypeOptions.find((ptfo) => ptfo.id === id)?.name;

  useEffect(() => {
    if (createStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Product Type created!', { variant: 'success' });
    }
    if (createStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Product Type creation error!', { variant: 'error' });
    }

    if (updateStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Product Type updated!', { variant: 'success' });
    }
    if (updateStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Product Type update error!', { variant: 'error' });
    }
  }, [createStatus, updateStatus]);

  const getFilesKey = (index: number) => `file-${index}`;

  const renderFileDeleteButton = (item: any, index, arrayHelpers) => (
    <IconButton
      edge="end"
      aria-label="delete"
      onClick={() => {
        arrayHelpers.remove(index);
      }}
    >
      <DeleteIcon />
    </IconButton>
  );

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>{initialValue?.id ? 'Edit Product Type class' : 'Create a new Product Type'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {error && <Alert severity="error">{error.message}</Alert>}
          Enter the product type details
        </DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id={productTypeFormFields.nameEn}
            name={productTypeFormFields.nameEn}
            label="Product Type Name En"
            value={values[productTypeFormFields.nameEn]}
            onChange={handleChange}
            error={touched.nameEn && !!errors.nameEn}
            helperText={touched.nameEn && errors.nameEn}
            autoComplete="productType-nameEn"
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            id={productTypeFormFields.nameAr}
            name={productTypeFormFields.nameAr}
            label="Product Type Name Ar"
            value={values[productTypeFormFields.nameAr]}
            onChange={handleChange}
            error={touched.nameAr && !!errors.nameAr}
            helperText={touched.nameAr && errors.nameAr}
            autoComplete="productType-nameAr"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            multiline
            id={productTypeFormFields.descriptionEn}
            name={productTypeFormFields.descriptionEn}
            label="Product Type Description En"
            value={values[productTypeFormFields.descriptionEn]}
            onChange={handleChange}
            error={touched.descriptionEn && !!errors.descriptionEn}
            helperText={touched.descriptionEn && errors.descriptionEn}
            autoComplete="productType-descriptionEn"
          />
          <TextField
            dir="rtl"
            margin="normal"
            required
            fullWidth
            multiline
            id={productTypeFormFields.descriptionAr}
            name={productTypeFormFields.descriptionAr}
            label="Product Type Description Ar"
            value={values[productTypeFormFields.descriptionAr]}
            onChange={handleChange}
            error={touched.descriptionAr && !!errors.descriptionAr}
            helperText={touched.descriptionAr && errors.descriptionAr}
            autoComplete="productType-descriptionAr"
          />
          <TextField
            margin="normal"
            type="number"
            required
            fullWidth
            id={productTypeFormFields.fee}
            name={productTypeFormFields.fee}
            label="Fee"
            value={values[productTypeFormFields.fee]}
            onChange={handleChange}
            error={touched.fee && !!errors.fee}
            helperText={touched.fee && errors.fee}
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="product-status-label">Product Status</InputLabel>
            <Select
              labelId="product-status-label"
              id={productTypeFormFields.productStatus}
              name={productTypeFormFields.productStatus}
              label="Product Status"
              value={values[productTypeFormFields.productStatus] ?? ProductStatus.OPERATIONAL}
              onChange={(e: SelectChangeEvent<string>) =>
                setFieldValue(productTypeFormFields.productStatus, e.target.value as ProductStatus)
              }
            >
              {Object.values(ProductStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
            {touched.productStatus && errors.productStatus && (
              <FormHelperText error>{errors.productStatus}</FormHelperText>
            )}
          </FormControl>
          <FormControlLabel
            label="Disabled"
            labelPlacement="start"
            componentsProps={{
              typography: { textTransform: 'capitalize' },
            }}
            name={productTypeFormFields.disabled}
            id={productTypeFormFields.disabled}
            control={
              <Switch
                name={productTypeFormFields.disabled}
                checked={values[productTypeFormFields.disabled]}
                onChange={handleChange}
              />
            }
          />
          <FormControlLabel
            label="Score Dependent"
            labelPlacement="start"
            componentsProps={{
              typography: { textTransform: 'capitalize' },
            }}
            name={productTypeFormFields.scoreDependent}
            id={productTypeFormFields.scoreDependent}
            control={
              <Switch
                name={productTypeFormFields.scoreDependent}
                checked={values[productTypeFormFields.scoreDependent]}
                onChange={handleChange}
              />
            }
          />
          <FormControlLabel
            label="Custom Product"
            labelPlacement="start"
            componentsProps={{
              typography: { textTransform: 'capitalize' },
            }}
            name={productTypeFormFields.isCustom}
            id={productTypeFormFields.isCustom}
            control={
              <Switch
                name={productTypeFormFields.isCustom}
                checked={values[productTypeFormFields.isCustom]}
                onChange={handleChange}
              />
            }
          />
          {values[productTypeFormFields.isCustom] ? (
            <ProductTypeIndexFundsAutocomplete
              id="product-type-index-funds"
              label="Index Funds"
              value={values[productTypeFormFields.indexFunds] || []}
              onChange={(ids) => setFieldValue(productTypeFormFields.indexFunds, ids)}
              mode={initialValue?.id ? 'edit' : 'create'}
              productTypeId={initialValue?.id || undefined}
              enabled={values[productTypeFormFields.isCustom]}
            />
          ) : null}
          <Grid item xs={12} md={12} lg={12}>
            <Paper sx={{ width: '100%', height: '100%', padding: 2 }}>
              <Typography color="text.primary" component="h4" gutterBottom>
                Files
              </Typography>
              <FormikProvider value={formik}>
                <FieldArray
                  name="files"
                  render={(arrayHelpers) => (
                    <>
                      <List>
                        {values[productTypeFormFields.files].map((item, index) => (
                          <ListItem
                            key={getFilesKey(index)}
                            secondaryAction={renderFileDeleteButton(item, index, arrayHelpers)}
                          >
                            <ListItemAvatar>
                              <Avatar>
                                <UploadFileButton
                                  accept="application/pdf"
                                  selectedFile={item.documentLink}
                                  uploadEntity={UploadEntity.indexFund}
                                  multiple
                                  onChange={(uploadedFiles) => {
                                    if (uploadedFiles.length) {
                                      for (let i = 0; i < uploadedFiles.length; i++) {
                                        if (index + i > values[productTypeFormFields.files].length - 1) {
                                          arrayHelpers.push({ documentLink: '' });
                                        }
                                        setFieldValue(`files.${index + i}.documentLink`, uploadedFiles[i]);
                                      }
                                    }
                                  }}
                                  onError={(uploadError) => {
                                    enqueueSnackbar(uploadError, { variant: 'error' });
                                  }}
                                />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                getFileTypeName(item.fileType) ||
                                (errors.files?.[index] as FormikErrors<UpdateProductTypeFile>)?.documentLink
                              }
                              secondary={item.documentLink}
                              primaryTypographyProps={{ fontWeight: 600 }}
                              secondaryTypographyProps={{ color: item.documentLink ? 'unset' : 'red' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {/* <Box>
                        <Button variant="outlined" onClick={() => arrayHelpers.push({ documentLink: '' })}>
                          Add
                        </Button>
                      </Box> */}
                    </>
                  )}
                />
                {!!errors.files && typeof errors.files === 'string' && (
                  <FormHelperText error>{errors.files}</FormHelperText>
                )}
              </FormikProvider>
            </Paper>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={createStatus === fetchStatuses.pending || updateStatus === fetchStatuses.pending}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
